import { CartUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

/**
 * Adds or removes an upsell product from the cart when its toggle changes.
 * Cart totals update via CartUpdateEvent; the product stays in the upsell list.
 */
class CartUpsellToggle extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#input?.addEventListener("change", this.#onChange);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener("change", this.#onChange);
    this.#controller?.abort();
  }

  /** @returns {HTMLInputElement | null} */
  get #input() {
    return this.querySelector("input[type='checkbox']");
  }

  get #cartItems() {
    return this.closest("cart-items-component");
  }

  #onChange = async () => {
    const input = this.#input;
    if (!input || input.disabled) return;

    this.#controller?.abort();
    this.#controller = new AbortController();
    input.disabled = true;
    this.#cartItems?.classList.add("cart-items-disabled");

    try {
      if (input.checked) {
        await this.#addToCart();
      } else {
        await this.#removeFromCart();
      }
    } catch (error) {
      console.error(error);
      input.checked = !input.checked;
    } finally {
      input.disabled = false;
      this.#cartItems?.classList.remove("cart-items-disabled");
    }
  };

  #sectionIds() {
    const ids = new Set();
    for (const item of document.querySelectorAll("cart-items-component")) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.add(item.dataset.sectionId);
      }
    }
    return Array.from(ids);
  }

  #payloadExtras() {
    return {
      sections: this.#sectionIds().join(","),
      sections_url: window.location.pathname,
    };
  }

  async #addToCart() {
    const variantId = this.dataset.variantId;
    if (!variantId) throw new Error("Missing variant id");

    const parsed = await this.#request(Theme.routes.cart_add_url, {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: { _cart_upsell: "true" },
        },
      ],
      ...this.#payloadExtras(),
    });

    if (parsed.status) {
      throw new Error(parsed.description || parsed.message || "Add failed");
    }

    this.#dispatchCartUpdate(parsed);
  }

  async #removeFromCart() {
    const updates = {};
    const ids = (this.dataset.updateIds || this.dataset.variantId || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    for (const id of ids) {
      updates[id] = 0;
    }

    if (Object.keys(updates).length === 0) return;

    const parsed = await this.#request(Theme.routes.cart_update_url, {
      updates,
      ...this.#payloadExtras(),
    });

    if (parsed.errors) {
      throw new Error(
        typeof parsed.errors === "string"
          ? parsed.errors
          : "Remove from cart failed",
      );
    }

    this.#dispatchCartUpdate(parsed);
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} body
   */
  async #request(url, body) {
    const response = await fetch(url, {
      ...fetchConfig("json", { body: JSON.stringify(body) }),
      signal: this.#controller?.signal,
    });
    return response.json();
  }

  /** @param {Record<string, unknown>} parsed */
  #dispatchCartUpdate(parsed) {
    const sourceId = this.#cartItems?.dataset.sectionId || "cart-upsell-toggle";
    this.dispatchEvent(
      new CartUpdateEvent(parsed, sourceId, {
        source: "cart-upsell-toggle",
        sections: parsed.sections,
        itemCount: parsed.item_count,
        didError: false,
      }),
    );
  }
}

if (!customElements.get("cart-upsell-toggle")) {
  customElements.define("cart-upsell-toggle", CartUpsellToggle);
}
